<template>
  <div class="row">
    <div class="col-12">
      <div class="row">
        <div class="col-sm-12">
          <!-- Two Factor Auth Start -->
          <div class="card">
            <div class="card-body">
              <div class="card-header-2 mb-3">
                <h5>Google Authenticator 2FA</h5>
              </div>

              <p class="mb-2">
                Status:
                <strong>{{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}</strong>
              </p>

              <div class="d-flex flex-wrap gap-2 mb-3">
                <button class="btn btn-sm btn-outline-primary" @click.prevent="generateQr">Generate / Regenerate QR</button>
                <button class="btn btn-sm btn-primary" @click.prevent="enable2fa" :disabled="!manualSecret || submitted">Enable 2FA</button>
                <button class="btn btn-sm btn-outline-danger" @click.prevent="disable2fa" :disabled="submitted || !twoFactorEnabled">Disable 2FA</button>
              </div>

              <div v-if="manualSecret" class="mb-3">
                <p class="mb-1">Manual setup code: <strong>{{ manualSecret }}</strong></p>
                <p class="mb-1">Enter a current code from Google Authenticator:</p>
                <input class="form-control" v-model="otpCode" type="text" maxlength="6" placeholder="123456" />
              </div>

              <div v-if="otpAuthUri" class="text-center mb-2">
                <img :src="otpQrUrl" alt="2FA QR" style="max-width: 220px; width: 100%; height: auto;" />
              </div>

              <p v-if="message" class="mb-0">{{ message }}</p>
            </div>
          </div>
          <!-- Two Factor Auth End -->

          <!-- Profile & Password Start -->
          <div class="card">
            <div class="card-body">
              <div class="card-header-2 mb-3">
                <h5>Authenticated Admin Profile</h5>
              </div>

              <form class="theme-form theme-form-2 mega-form" @submit.prevent="saveProfile">
                <div class="row">
                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">First Name</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="profileForm.name" type="text" placeholder="Enter your first name" />
                    </div>
                  </div>

                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">Last Name</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="profileForm.lastname" type="text" placeholder="Enter your last name" />
                    </div>
                  </div>

                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">Phone Number</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="profileForm.phone" type="text" placeholder="Enter your phone number" />
                    </div>
                  </div>

                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">Email</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="profileForm.email" type="email" placeholder="Enter your email" />
                    </div>
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-primary" type="submit" :disabled="savingProfile">Save Profile</button>
                </div>
              </form>

              <p v-if="profileMessage" class="mt-3 mb-0 text-success">{{ profileMessage }}</p>
              <p v-if="profileError" class="mt-2 mb-0 text-danger">{{ profileError }}</p>

              <hr class="my-4" />

              <div class="card-header-2 mb-3">
                <h5>Change Password</h5>
              </div>

              <form class="theme-form theme-form-2 mega-form" @submit.prevent="changePassword">
                <div class="row">
                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">Current Password</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="passwordForm.currentPassword" type="password" placeholder="Enter your current password" />
                    </div>
                  </div>

                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">New Password</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="passwordForm.newPassword" type="password" placeholder="Enter your new password" />
                    </div>
                  </div>

                  <div class="mb-4 row align-items-center">
                    <label class="form-label-title col-sm-2 mb-0">Confirm New Password</label>
                    <div class="col-sm-10">
                      <input class="form-control" v-model="passwordForm.confirmPassword" type="password" placeholder="Confirm your new password" />
                    </div>
                  </div>
                </div>

                <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-primary" type="submit" :disabled="savingPassword">Update Password</button>
                </div>
              </form>

              <p v-if="passwordMessage" class="mt-3 mb-0 text-success">{{ passwordMessage }}</p>
              <p v-if="passwordError" class="mt-2 mb-0 text-danger">{{ passwordError }}</p>
            </div>
          </div>
          <!-- Profile & Password End -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import QRCode from 'qrcode'

export default {
  title: "Voxo-Profile Setting",
  data() {
    return {
      twoFactorEnabled: false,
      manualSecret: null,
      otpAuthUri: null,
      otpQrDataUrl: '',
      otpCode: '',
      submitted: false,
      message: '',
      profileForm: {
        name: '',
        lastname: '',
        phone: '',
        email: '',
      },
      profileMessage: '',
      profileError: '',
      savingProfile: false,
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      passwordMessage: '',
      passwordError: '',
      savingPassword: false,
    }
  },
  computed: {
    otpQrUrl() {
      return this.otpQrDataUrl
    },
  },
  methods: {
    getApiBase() {
      return process.env.VUE_APP_URL_API_BASE || process.env.VUE_APP_URL_BASE_API || ''
    },
    parsePayload(payload) {
      if (typeof payload === 'string') {
        try {
          return JSON.parse(payload)
        } catch (e) {
          return {}
        }
      }

      return payload || {}
    },
    async buildQrDataUrl() {
      if (!this.otpAuthUri) {
        this.otpQrDataUrl = ''
        return
      }

      try {
        this.otpQrDataUrl = await QRCode.toDataURL(this.otpAuthUri, {
          width: 240,
          margin: 1,
        })
      } catch (e) {
        this.otpQrDataUrl = ''
      }
    },
    async fetch2faStatus() {
      const response = await axios.get(this.getApiBase() + '/admin/2fa/status')
      this.twoFactorEnabled = response.data && response.data.enabled === true
    },
    async fetchAdminProfile() {
      this.profileError = ''
      try {
        const response = await axios.get(this.getApiBase() + '/admin/admin-profile')
        const profile = this.parsePayload(response.data)
        this.profileForm.name = profile.name || ''
        this.profileForm.lastname = profile.lastname || ''
        this.profileForm.phone = profile.phone || ''
        this.profileForm.email = profile.email || ''
      } catch (e) {
        this.profileError = 'Could not load authenticated admin profile.'
      }
    },
    async saveProfile() {
      this.profileMessage = ''
      this.profileError = ''

      if (!this.profileForm.name || !this.profileForm.email) {
        this.profileError = 'Name and email are required.'
        return
      }

      this.savingProfile = true
      try {
        const response = await axios.post(this.getApiBase() + '/admin/profile/update', {
          name: this.profileForm.name,
          lastname: this.profileForm.lastname,
          phone: this.profileForm.phone,
          email: this.profileForm.email,
        })

        this.profileMessage = (response.data && (response.data.message || response.data.msg)) || 'Profile updated successfully.'

        const profile = this.parsePayload(response.data && response.data.profile)
        if (profile && profile.email) {
          this.profileForm.name = profile.name || this.profileForm.name
          this.profileForm.lastname = profile.lastname || ''
          this.profileForm.phone = profile.phone || ''
          this.profileForm.email = profile.email || this.profileForm.email
        }
      } catch (e) {
        this.profileError = (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Could not update profile.'
      } finally {
        this.savingProfile = false
      }
    },
    async changePassword() {
      this.passwordMessage = ''
      this.passwordError = ''

      if (!this.passwordForm.currentPassword || !this.passwordForm.newPassword || !this.passwordForm.confirmPassword) {
        this.passwordError = 'All password fields are required.'
        return
      }

      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = 'New password and confirmation do not match.'
        return
      }

      this.savingPassword = true
      try {
        const response = await axios.post(this.getApiBase() + '/admin/profile/change-password', {
          currentPassword: this.passwordForm.currentPassword,
          newPassword: this.passwordForm.newPassword,
          confirmPassword: this.passwordForm.confirmPassword,
        })

        this.passwordMessage = (response.data && (response.data.message || response.data.msg)) || 'Password updated successfully.'
        this.passwordForm.currentPassword = ''
        this.passwordForm.newPassword = ''
        this.passwordForm.confirmPassword = ''
      } catch (e) {
        this.passwordError = (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Could not update password.'
      } finally {
        this.savingPassword = false
      }
    },
    async generateQr() {
      this.message = ''
      this.submitted = true
      try {
        const response = await axios.post(this.getApiBase() + '/admin/2fa/setup', {})
        this.manualSecret = response.data.manualSecret || null
        this.otpAuthUri = response.data.otpAuthUri || null
        await this.buildQrDataUrl()
        this.twoFactorEnabled = false
        this.message = 'QR generated. Scan it and then click Enable 2FA with your code.'
      } catch (e) {
        this.message = 'Could not generate QR.'
      } finally {
        this.submitted = false
      }
    },
    async enable2fa() {
      this.message = ''
      this.submitted = true
      try {
        await axios.post(this.getApiBase() + '/admin/2fa/enable', {
          code: this.otpCode,
        })
        this.twoFactorEnabled = true
        this.message = '2FA enabled.'
      } catch (e) {
        this.message = 'Invalid code. Could not enable 2FA.'
      } finally {
        this.submitted = false
      }
    },
    async disable2fa() {
      this.message = ''
      this.submitted = true
      try {
        await axios.post(this.getApiBase() + '/admin/2fa/disable', {
          code: this.otpCode,
        })
        this.twoFactorEnabled = false
        this.message = '2FA disabled.'
      } catch (e) {
        this.message = 'Invalid code. Could not disable 2FA.'
      } finally {
        this.submitted = false
      }
    },
  },
  async mounted() {
    await this.fetch2faStatus()
    await this.fetchAdminProfile()
  },
};
</script>

<style></style>
